import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, Input, Select } from '@/shared/ui'
import {
  integrationsApi,
  useAction,
  type Catalog,
  type Source,
  type SourceInput,
  type SourceTestResult,
} from '@/entities/integration'
import {
  AuthEditor,
  BitrixLogo,
  BitrixUrlInput,
  CheckIcon,
  ChoiceCard,
  ConditionsEditor,
  DataIcon,
  Drawer,
  JsonView,
  KVEditor,
  Label,
  PlayIcon,
  PlusIcon,
  RetryIcon,
  StatusPill,
  Step,
  timeAgo,
  TrashIcon,
  TreePicker,
  useInsert,
  useSecrets,
  VariableChips,
} from './kit'
import s from './integrations.module.css'

export function SourcesTab({ catalog, sources }: { catalog: Catalog; sources: Source[] }) {
  const [editing, setEditing] = useState<{ src: Source | null; kind: 'http' | 'bitrix'; preset?: string } | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  return (
    <>
      <div className={s.sectionHead}>
        <div>
          <h2 className={s.sectionTitle}>Данные для бота</h2>
          <p className={s.sectionText}>
            Бот заранее знает, что у вас есть: свободные объекты, цены, сделка клиента. Подключите источник — мы заберём
            данные и положим их боту в разговор. Если источник не ответит за 2–3 секунды, звонок идёт без них.
          </p>
        </div>
        {sources.length > 0 && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={() => setPreviewOpen(true)}>
              Что видит бот
            </Button>
            <Button onClick={() => setEditing({ src: null, kind: 'http' })}>
              <span className={s.btnRow}><PlusIcon /> Добавить</span>
            </Button>
          </div>
        )}
      </div>

      {sources.length === 0 ? (
        <div className={s.starters}>
          {catalog.bitrixPresets.map((p) => (
            <button key={p.key} type="button" className={s.starter} onClick={() => setEditing({ src: null, kind: 'bitrix', preset: p.key })}>
              <span className={`${s.cardIcon} ${s.cardIconBitrix}`}>
                <BitrixLogo />
              </span>
              <span className={s.starterTitle}>{p.title}</span>
              <span className={s.starterText}>{p.description}</span>
            </button>
          ))}
          <button type="button" className={s.starter} onClick={() => setEditing({ src: null, kind: 'http' })}>
            <span className={`${s.cardIcon} ${s.cardIconBrand}`}>
              <DataIcon />
            </span>
            <span className={s.starterTitle}>Любая система по ссылке</span>
            <span className={s.starterText}>Ваш API, Google-таблица, 1С, amoCRM — всё, что отдаёт JSON.</span>
          </button>
        </div>
      ) : (
        <div className={s.cards}>
          {sources.map((src) => (
            <SourceCard key={src.id} src={src} onOpen={() => setEditing({ src, kind: src.kind })} />
          ))}
          <button type="button" className={s.addCard} onClick={() => setEditing({ src: null, kind: 'http' })}>
            <span className={s.addCardIcon}>
              <PlusIcon />
            </span>
            Новый источник
          </button>
        </div>
      )}

      {editing && (
        <SourceEditor catalog={catalog} src={editing.src} initialKind={editing.kind} presetKey={editing.preset}
          onClose={() => setEditing(null)} />
      )}
      {previewOpen && <BotPreview catalog={catalog} onClose={() => setPreviewOpen(false)} />}
    </>
  )
}

function SourceCard({ src, onOpen }: { src: Source; onOpen: () => void }) {
  const run = useAction(() => integrationsApi.runSource(src.id), (r) => (r.lastError ? undefined : `Обновлено: записей ${r.lastItems}`))
  const bitrix = src.kind === 'bitrix'
  return (
    <div className={`${s.card} ${src.enabled ? '' : s.cardOff}`} onClick={onOpen} role="button" tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}>
      <div className={s.cardTop}>
        <span className={`${s.cardIcon} ${bitrix ? s.cardIconBitrix : s.cardIconBrand}`}>{bitrix ? <BitrixLogo /> : <DataIcon />}</span>
        <div className={s.cardTitles}>
          <h3 className={s.cardTitle}>{src.name}</h3>
          <div className={s.cardSub}>{bitrix ? `Битрикс24 · ${src.request.url}` : src.request.url}</div>
        </div>
        <StatusPill status={!src.enabled ? 'off' : src.lastError ? 'error' : 'on'} />
      </div>
      {src.lastError && <div className={`${s.alert} ${s.alertBad}`}>{src.lastError}</div>}
      <div className={s.cardChips}>
        <span className={s.chip}>{src.mode === 'per_call' ? 'перед каждым звонком' : `раз в ${src.refreshMin} мин`}</span>
        <span className={s.chip}>полей: {src.shape.fields.length || 'все'}</span>
        <span className={s.chip}>{src.bots.length ? `ботов: ${src.bots.length}` : 'все боты'}</span>
      </div>
      {src.lastText && (
        <div className={s.botText} style={{ maxHeight: 92, fontSize: 12, overflow: 'hidden' }}>
          {src.lastText}
        </div>
      )}
      <div className={s.cardFoot}>
        <span>
          {src.mode === 'cached' ? `Записей: ${src.lastItems} · обновлено ${timeAgo(src.lastOkAt)}` : `Последний запрос ${timeAgo(src.lastRunAt)}`}
        </span>
        {src.mode === 'cached' && (
          <button type="button" className={s.addLink} disabled={run.isPending}
            onClick={(e) => (e.stopPropagation(), run.mutate(undefined))}>
            {run.isPending ? 'Обновляем…' : 'Обновить сейчас'}
          </button>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- Editor */

function SourceEditor({
  catalog,
  src,
  initialKind,
  presetKey,
  onClose,
}: {
  catalog: Catalog
  src: Source | null
  initialKind: 'http' | 'bitrix'
  presetKey?: string
  onClose: () => void
}) {
  const preset = catalog.bitrixPresets.find((p) => p.key === presetKey)
  const [form, setForm] = useState<SourceInput>(() =>
    src
      ? {
          // только поля формы: сервер строго отклоняет лишнее (id, статистику)
          name: src.name,
          enabled: src.enabled,
          kind: src.kind,
          request: src.request,
          auth: src.auth,
          secrets: {},
          mode: src.mode,
          refreshMin: src.refreshMin,
          timeoutMs: src.timeoutMs,
          shape: src.shape,
          bots: src.bots ?? [],
        }
      : {
          name: preset?.title ?? (initialKind === 'bitrix' ? 'Данные из Битрикса' : 'Мой источник'),
          enabled: true,
          kind: initialKind,
          request: preset
            ? { method: 'POST', url: preset.method, body: preset.body, headers: [] }
            : { method: initialKind === 'bitrix' ? 'POST' : 'GET', url: '', body: '', headers: [] },
          auth: { kind: initialKind === 'bitrix' ? 'bitrix' : 'none' },
          secrets: {},
          mode: preset?.perCall ? 'per_call' : 'cached',
          refreshMin: 15,
          timeoutMs: 2500,
          shape: { itemsPath: preset?.itemsPath ?? '', fields: [], filters: [], limit: 20, intro: '' },
          bots: [],
        },
  )
  const { secrets, setSecret } = useSecrets()
  const [test, setTest] = useState<SourceTestResult | null>(null)
  const [contactId, setContactId] = useState(0)

  const set = <K extends keyof SourceInput>(k: K, v: SourceInput[K]) => setForm((f) => ({ ...f, [k]: v }))
  const setShape = (patch: Partial<SourceInput['shape']>) => setForm((f) => ({ ...f, shape: { ...f.shape, ...patch } }))
  const bitrix = form.kind === 'bitrix'

  const payload = (): SourceInput => {
    const sec: Record<string, string> = {}
    for (const [k, v] of Object.entries(secrets)) if (v) sec[k] = v
    return { ...form, secrets: sec, auth: bitrix ? { kind: 'bitrix' } : form.auth }
  }

  const testMut = useAction(() => integrationsApi.testSource(src?.id ?? null, payload(), contactId || undefined))
  const save = useAction(() => integrationsApi.saveSource(src?.id ?? null, payload()), src ? 'Сохранено' : 'Источник подключён')
  const remove = useAction(() => integrationsApi.deleteSource(src!.id), 'Удалено')
  const oauth = useAction(() => integrationsApi.oauthStart('source', src!.id))

  const runTest = () => testMut.mutate(undefined, { onSuccess: (r) => setTest(r) })

  const items = test?.response && form.shape.itemsPath ? getPath(test.response, form.shape.itemsPath) : null
  const treeRoot = Array.isArray(items) ? items : test?.response
  const rootPath = Array.isArray(items) ? form.shape.itemsPath : ''

  const pickField = (path: string, sample: string, isArray: boolean) => {
    if (isArray) {
      setShape({ itemsPath: form.shape.itemsPath === path ? '' : path, fields: [] })
      return
    }
    // путь внутри записи: items[0].title → title
    let rel = path
    if (form.shape.itemsPath && path.startsWith(form.shape.itemsPath + '[0]')) rel = path.slice(form.shape.itemsPath.length + 3).replace(/^\./, '')
    const has = form.shape.fields.some((f) => f.path === rel)
    setShape({
      fields: has ? form.shape.fields.filter((f) => f.path !== rel) : [...form.shape.fields, { path: rel, label: guessLabel(rel, sample) }],
    })
  }
  const selected = form.shape.fields.map((f) => (form.shape.itemsPath ? `${form.shape.itemsPath}[0]${f.path ? '.' + f.path : ''}` : f.path))

  const { ref: urlRef, insert: insertUrl } = useInsert<HTMLInputElement>(form.request.url, (v) => set('request', { ...form.request, url: v }))

  const itemFieldOpts = (test?.itemFields ?? []).map((f) => ({ value: f.path, label: `${f.path} (${f.sample})` }))

  const contacts = useQuery({
    queryKey: ['integrations', 'test-contacts'],
    queryFn: () => integrationsApi.sourcesPreview(),
    enabled: false,
  })
  void contacts

  const footer = (
    <>
      {src && (
        <Button variant="ghost" loading={remove.isPending}
          onClick={() => window.confirm(`Удалить источник «${src.name}»? Бот перестанет получать эти данные.`) && remove.mutate(undefined, { onSuccess: onClose })}>
          Удалить
        </Button>
      )}
      <span className={s.spacer} />
      <Button variant="secondary" onClick={onClose}>
        Отмена
      </Button>
      <Button onClick={() => save.mutate(undefined, { onSuccess: onClose })} loading={save.isPending}>
        <span className={s.btnRow}><CheckIcon /> {src ? 'Сохранить' : 'Подключить'}</span>
      </Button>
    </>
  )

  let n = 1
  return (
    <Drawer title={src ? src.name : 'Новый источник данных'} subtitle="Откуда бот берёт актуальные данные"
      icon={bitrix ? <BitrixLogo size={24} /> : <DataIcon />} onClose={onClose} footer={footer} wide>
      {!src && (
        <div className={s.choices}>
          <ChoiceCard active={bitrix} icon={<BitrixLogo />} title="Битрикс24" text="Сделки, товары, смарт-процессы — выбираете из списка"
            onClick={() => setForm((f) => ({ ...f, kind: 'bitrix', auth: { kind: 'bitrix' }, request: { method: 'POST', url: catalog.bitrixPresets[0]?.method ?? '', body: catalog.bitrixPresets[0]?.body ?? '' } }))} />
          <ChoiceCard active={!bitrix} icon={<DataIcon />} title="Любая система" text="Ссылка на API, который отдаёт JSON"
            onClick={() => setForm((f) => ({ ...f, kind: 'http', auth: { kind: 'none' }, request: { method: 'GET', url: '', body: '' } }))} />
        </div>
      )}

      <Step n={n++} title="Название" hint="Так бот увидит этот блок: «Свободные объекты», «Прайс», «Сделка клиента».">
        <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
      </Step>

      <Step n={n++} title={bitrix ? 'Что взять из Битрикса' : 'Откуда брать'}>
        {bitrix ? (
          <>
            <BitrixUrlInput hint={src?.secrets.bitrix_url} value={secrets.bitrix_url} onChange={(v) => setSecret('bitrix_url', v)} />
            <div className={s.templates}>
              {catalog.bitrixPresets.map((p) => (
                <button key={p.key} type="button" className={`${s.tpl} ${form.request.url === p.method && form.request.body === p.body ? s.tplActive : ''}`}
                  onClick={() => {
                    setForm((f) => ({
                      ...f,
                      name: f.name === 'Данные из Битрикса' || catalog.bitrixPresets.some((x) => x.title === f.name) ? p.title : f.name,
                      request: { method: 'POST', url: p.method, body: p.body },
                      mode: p.perCall ? 'per_call' : f.mode,
                      shape: { ...f.shape, itemsPath: p.itemsPath, fields: [] },
                    }))
                    setTest(null)
                  }}>
                  <span className={s.tplTitle}>{p.title}</span>
                  <span className={s.tplText}>{p.description}</span>
                </button>
              ))}
            </div>
            <details>
              <summary className={s.addLink} style={{ cursor: 'pointer' }}>Тонкая настройка запроса</summary>
              <div className={s.grid2} style={{ marginTop: 8 }}>
                <div className={s.formRow}>
                  <Label>Метод REST</Label>
                  <Input value={form.request.url} onChange={(e) => set('request', { ...form.request, url: e.target.value })} placeholder="crm.deal.list" />
                </div>
              </div>
              <div className={s.formRow} style={{ marginTop: 8 }}>
                <Label hint="filter, select, order — как в документации Битрикса">Параметры</Label>
                <textarea className={s.textarea} rows={4} value={form.request.body ?? ''}
                  onChange={(e) => set('request', { ...form.request, body: e.target.value })} spellCheck={false} />
              </div>
            </details>
          </>
        ) : (
          <>
            <div className={s.urlRow}>
              <Select value={form.request.method} onChange={(e) => set('request', { ...form.request, method: e.target.value })}
                options={['GET', 'POST'].map((m) => ({ value: m, label: m }))} />
              <input ref={urlRef} className={s.textarea} style={{ minHeight: 40, height: 40, resize: 'none', padding: '0 12px' }}
                value={form.request.url} placeholder="https://api.example.ru/objects?status=free"
                onChange={(e) => set('request', { ...form.request, url: e.target.value })} />
            </div>
            <VariableChips variables={catalog.contactVariables} onInsert={insertUrl} limit={8} />
            {form.request.method === 'POST' && (
              <div className={s.formRow}>
                <Label>Тело запроса (JSON)</Label>
                <textarea className={s.textarea} rows={4} value={form.request.body ?? ''}
                  onChange={(e) => set('request', { ...form.request, body: e.target.value })} />
              </div>
            )}
            <details>
              <summary className={s.addLink} style={{ cursor: 'pointer' }}>Свои заголовки</summary>
              <div style={{ marginTop: 8 }}>
                <KVEditor items={form.request.headers ?? []} onChange={(v) => set('request', { ...form.request, headers: v })} />
              </div>
            </details>
          </>
        )}
      </Step>

      {!bitrix && (
        <Step n={n++} title="Авторизация" hint="Ключи храним зашифрованными, в журналах их нет.">
          <AuthEditor auth={form.auth} setAuth={(a) => set('auth', a)} secrets={secrets} setSecret={setSecret}
            hints={src?.secrets ?? {}} kinds={catalog.authKinds.filter((k) => k !== 'bitrix' && k !== 'hmac')}
            onConnect={src ? () => oauth.mutate(undefined, { onSuccess: (r) => (window.location.href = r.url) }) : undefined} />
        </Step>
      )}

      <Step n={n++} title="Проверить и выбрать поля" hint="Сходим за данными и покажем ответ. Кликните, какие поля нужны боту."
        aside={
          <Button variant="secondary" size="sm" onClick={runTest} loading={testMut.isPending}>
            <span className={s.btnRow}><PlayIcon /> Проверить</span>
          </Button>
        }>
        {form.mode === 'per_call' && (
          <div className={s.formRow}>
            <Label hint="Подставим его телефон и данные в запрос">На каком клиенте проверить</Label>
            <Input type="number" placeholder="ID клиента из карточки (пусто — последний, кому звонили)" value={contactId || ''}
              onChange={(e) => setContactId(Number(e.target.value) || 0)} />
          </div>
        )}
        {!test && <span className={s.muted}>Нажмите «Проверить» — появится дерево ответа.</span>}
        {test && (
          <>
            <div className={`${s.alert} ${test.ok ? s.alertOk : s.alertBad}`}>
              {test.ok
                ? `Ответ получен за ${test.durationMs} мс${test.items ? ` · записей: ${test.items}` : ''}${test.contact ? ` · клиент: ${test.contact}` : ''}`
                : test.error || `Сервер ответил ${test.status}`}
            </div>
            {test.ok && test.response != null && (
              <>
                {test.itemPaths.length > 0 && !form.shape.itemsPath && (
                  <div className={`${s.alert} ${s.alertInfo}`}>
                    В ответе есть список. Нажмите «Это список записей» рядом с ним — тогда бот увидит каждую запись отдельно.
                  </div>
                )}
                <TreePicker data={treeRoot} rootPath={rootPath} selected={selected} onPick={pickField} pickArrays={!form.shape.itemsPath} />
                {form.shape.itemsPath && (
                  <button type="button" className={s.addLink} onClick={() => setShape({ itemsPath: '', fields: [] })}>
                    ← Показать весь ответ
                  </button>
                )}
              </>
            )}
            {!test.ok && test.rawBody && <JsonView value={test.rawBody} maxHeight={200} />}
          </>
        )}
        {form.shape.fields.length > 0 && (
          <div className={s.formRow}>
            <Label hint="Как поле подписать для бота">Выбранные поля</Label>
            {form.shape.fields.map((f, i) => (
              <div key={f.path} className={s.mapRow}>
                <code className={s.mono} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.path || '(значение)'}</code>
                <span className={s.mapArrow}>→</span>
                <Input value={f.label} onChange={(e) => setShape({ fields: form.shape.fields.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                <button type="button" className={s.iconBtn} onClick={() => setShape({ fields: form.shape.fields.filter((_, j) => j !== i) })}>
                  <TrashIcon />
                </button>
              </div>
            ))}
          </div>
        )}
      </Step>

      {form.shape.itemsPath && (
        <Step n={n++} title="Какие записи брать" hint="Например, только свободные объекты, и не больше 20 штук.">
          <ConditionsEditor items={form.shape.filters} onChange={(v) => setShape({ filters: v })}
            fields={itemFieldOpts.length ? itemFieldOpts : form.shape.fields.map((f) => ({ value: f.path, label: f.path }))}
            operators={catalog.operators} emptyText="Берём все записи" />
          <div className={s.grid2}>
            <div className={s.formRow}>
              <Label>Не больше записей</Label>
              <Input type="number" min={1} max={100} value={form.shape.limit} onChange={(e) => setShape({ limit: Number(e.target.value) || 20 })} />
            </div>
          </div>
        </Step>
      )}

      <Step n={n++} title="Когда обновлять">
        <div className={s.choices}>
          <ChoiceCard active={form.mode === 'cached'} onClick={() => set('mode', 'cached')} title="Общие данные — раз в N минут"
            text="Свободные объекты, прайс, акции. Звонок не ждёт ваш сервер." />
          <ChoiceCard active={form.mode === 'per_call'} onClick={() => set('mode', 'per_call')} title="Перед каждым звонком"
            text="Данные именно этого клиента: его сделка, заказ, баланс." />
        </div>
        {form.mode === 'cached' ? (
          <div className={s.grid2}>
            <div className={s.formRow}>
              <Label>Обновлять каждые</Label>
              <Select value={String(form.refreshMin)} onChange={(e) => set('refreshMin', Number(e.target.value))}
                options={[1, 5, 15, 30, 60, 180, 720, 1440].map((m) => ({ value: String(m), label: m < 60 ? `${m} мин` : m === 1440 ? 'раз в сутки' : `${m / 60} ч` }))} />
            </div>
          </div>
        ) : (
          <div className={s.grid2}>
            <div className={s.formRow}>
              <Label hint="Не ответил — звоним без этих данных">Ждать ответ не дольше</Label>
              <Select value={String(form.timeoutMs)} onChange={(e) => set('timeoutMs', Number(e.target.value))}
                options={[1000, 1500, 2000, 2500, 3000, 5000].map((m) => ({ value: String(m), label: `${(m / 1000).toLocaleString('ru-RU')} сек` }))} />
            </div>
          </div>
        )}
      </Step>

      <Step n={n++} title="Как это увидит бот" hint="Этот текст попадёт боту перед разговором. В сценарии можно писать: «предлагай варианты из блока «…»».">
        <div className={s.formRow}>
          <Label>Пояснение для бота (необязательно)</Label>
          <Input value={form.shape.intro} onChange={(e) => setShape({ intro: e.target.value })}
            placeholder="Актуальные свободные объекты. Предлагай только их." />
        </div>
        {test?.text ? <pre className={s.botText}>{test.text}</pre> : <span className={s.muted}>Нажмите «Проверить» выше — покажем точный текст.</span>}
        <div className={s.formRow}>
          <Label>Каким ботам давать</Label>
          <div className={s.cardChips}>
            <button type="button" className={`${s.chip} ${form.bots.length === 0 ? s.chipBrand : ''}`} onClick={() => set('bots', [])}>
              Всем ботам
            </button>
            {catalog.bots.map((b) => {
              const on = form.bots.includes(b.id)
              return (
                <button key={b.id} type="button" className={`${s.chip} ${on ? s.chipBrand : ''}`}
                  onClick={() => set('bots', on ? form.bots.filter((x) => x !== b.id) : [...form.bots, b.id])}>
                  {on ? '✓ ' : ''}
                  {b.name}
                </button>
              )
            })}
          </div>
        </div>
      </Step>

      {src && <RunsBlock id={src.id} />}
    </Drawer>
  )
}

function RunsBlock({ id }: { id: number }) {
  const q = useQuery({ queryKey: ['integrations', 'source-runs', id], queryFn: () => integrationsApi.sourceRuns(id) })
  const items = q.data?.items ?? []
  return (
    <Step n="" title="Журнал запросов" hint="Последние обращения к источнику: когда, сколько заняло, что пошло не так.">
      {items.length === 0 ? (
        <span className={s.muted}>Запросов пока не было.</span>
      ) : (
        <div className={s.timeline}>
          {items.slice(0, 15).map((r) => (
            <div key={r.id} className={`${s.tlItem} ${r.ok ? s.tlOk : s.tlBad}`}>
              <div className={s.tlTitle}>
                {r.trigger === 'call' ? `Перед звонком${r.callId ? ` №${r.callId}` : ''}` : r.trigger === 'test' ? 'Проверка' : 'Обновление'} ·{' '}
                {r.ok ? `записей ${r.items}` : 'ошибка'} · {r.durationMs} мс
              </div>
              <div className={s.tlSub}>
                {timeAgo(r.createdAt)}
                {r.error ? ` — ${r.error}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </Step>
  )
}

function BotPreview({ catalog, onClose }: { catalog: Catalog; onClose: () => void }) {
  const [bot, setBot] = useState(0)
  const [contact, setContact] = useState(0)
  const q = useQuery({
    queryKey: ['integrations', 'bot-preview', bot, contact],
    queryFn: () => integrationsApi.sourcesPreview(contact || undefined, bot || undefined),
  })
  const botOpts = useMemo(() => [{ value: '0', label: 'Любой бот' }, ...catalog.bots.map((b) => ({ value: String(b.id), label: b.name }))], [catalog])
  return (
    <Drawer title="Что видит бот перед звонком" subtitle="Ровно этот текст добавляется к сценарию" icon={<DataIcon />} onClose={onClose}>
      <div className={s.grid2}>
        <div className={s.formRow}>
          <Label>Бот</Label>
          <Select value={String(bot)} onChange={(e) => setBot(Number(e.target.value))} options={botOpts} />
        </div>
        <div className={s.formRow}>
          <Label>ID клиента</Label>
          <Input type="number" value={contact || ''} placeholder="необязательно" onChange={(e) => setContact(Number(e.target.value) || 0)} />
        </div>
      </div>
      <div className={s.condActions}>
        <Button size="sm" variant="secondary" onClick={() => void q.refetch()} loading={q.isFetching}>
          <span className={s.btnRow}><RetryIcon /> Обновить</span>
        </Button>
      </div>
      {q.data?.text ? <pre className={s.botText}>{q.data.text}</pre> : <span className={s.muted}>{q.isLoading ? 'Собираем…' : 'Нет данных: источники выключены или пока пусты.'}</span>}
    </Drawer>
  )
}

/* ------------------------------------------------------------ helpers */

function getPath(obj: unknown, path: string): unknown {
  let cur: unknown = obj
  for (const part of path.split('.').filter(Boolean)) {
    const m = part.match(/^([^[]*)((?:\[\d+\])*)$/)
    if (!m) return undefined
    if (m[1]) {
      if (cur == null || typeof cur !== 'object') return undefined
      cur = (cur as Record<string, unknown>)[m[1]]
    }
    for (const idx of (m[2] ?? '').matchAll(/\[(\d+)\]/g)) {
      if (!Array.isArray(cur)) return undefined
      cur = cur[Number(idx[1])]
    }
  }
  return cur
}

const KNOWN: Record<string, string> = {
  TITLE: 'Название',
  NAME: 'Название',
  OPPORTUNITY: 'Сумма',
  PRICE: 'Цена',
  STAGE_ID: 'Стадия',
  COMMENTS: 'Комментарий',
  DESCRIPTION: 'Описание',
  ADDRESS: 'Адрес',
  CURRENCY_ID: 'Валюта',
  title: 'Название',
  name: 'Название',
  price: 'Цена',
  address: 'Адрес',
  status: 'Статус',
  area: 'Площадь',
  description: 'Описание',
}

function guessLabel(path: string, _sample: string) {
  const last = path.split('.').pop()?.replace(/\[\d+\]/g, '') ?? path
  return KNOWN[last] ?? last
}
